import { add, parse } from 'date-fns';
import * as yup from 'yup';
import { AnyObject, Maybe } from 'yup';

import { RegexConst } from '@nocowanie/core';

import { DATE_FORMAT } from '@app/consts';
import { creditCardsData } from '@app/models';
import { ConsentType } from '@app/models/api/api-types';

import { consentCheckboxSchema } from './checkout-consents.def';

export const getSchema = (
    t: (key: string) => string,
    creditCardRequired: boolean,
    guestAddressRequired: boolean,
) => {
    const requiredMessage = t('required');
    const incorrectPostCode = t('incorrectPostCode');
    const incorrectPhoneNumber = t('incorrectPhoneNumber');
    const incorrectEmail = t('incorrectEmail');
    const incorrectExpirationDate = t('incorrectExpirationDate');
    const incorrectCreditCardNumber = t('incorrectCreditCardNumber');

    const consentCheckbox = yup.object({ ...consentCheckboxSchema });
    const userDataSchema = {
        firstName: yup.string().trim().required(requiredMessage),
        lastName: yup.string().trim().required(requiredMessage),
        phoneNumber: yup
            .string()
            .trim()
            .required(requiredMessage)
            .matches(RegexConst.PHONE_NUMBER, {
                message: incorrectPhoneNumber,
            }),
        email: yup.string().trim().required(requiredMessage).email(incorrectEmail),
        settlement: guestAddressRequired
            ? yup.string().trim().required(requiredMessage)
            : yup.string().nullable().notRequired(),
        postalCode: guestAddressRequired
            ? yup.string().trim().required(requiredMessage).matches(RegexConst.POST_CODE, {
                  message: incorrectPostCode,
              })
            : yup.string().nullable().notRequired(),
        address: guestAddressRequired
            ? yup.string().trim().required(requiredMessage)
            : yup.string().nullable().notRequired(),
    };

    const creditCardSchema = {
        creditCardFirstName: creditCardRequired
            ? yup.string().trim().required(requiredMessage)
            : yup.string().nullable(),
        creditCardLastName: creditCardRequired
            ? yup.string().trim().required(requiredMessage)
            : yup.string().nullable(),
        creditCardNumber: creditCardRequired
            ? yup
                  .string()
                  .trim()
                  .required(requiredMessage)
                  .matches(RegexConst.NUMBERS_ONLY, {
                      message: incorrectCreditCardNumber,
                  })
                  .test('creditCardNumber', incorrectCreditCardNumber, (value: string): boolean => {
                      for (const [index, cardData] of Object.entries(creditCardsData)) {
                          const isValid = cardData.prefixes.some(prefix => {
                              if (value.startsWith(prefix)) {
                                  return cardData.digits.some(length => value.length === length);
                              }

                              return false;
                          });

                          if (isValid) {
                              return true;
                          }
                      }
                      return false;
                  })
            : yup.string().nullable(),
        creditCardExpirationDate: creditCardRequired
            ? yup
                  .string()
                  .trim()
                  .required(requiredMessage)
                  .test(
                      'creditCardExpirationDate',
                      incorrectExpirationDate,
                      (value: string): boolean => {
                          if (!value.match(RegexConst.CREDIT_CARD_EXPIRATION_DATE)) {
                              return false;
                          }

                          const today = new Date();
                          const expirationDate = add(
                              parse(value, DATE_FORMAT.expirationDateFormat, new Date()),
                              {
                                  months: 1,
                              },
                          );

                          return expirationDate >= today;
                      },
                  )
            : yup.string().nullable(),
    };

    const formSchema = yup.object({
        ...userDataSchema,
        consentCheckbox,
        ...creditCardSchema,
    });

    const isValidFn = <T extends Maybe<AnyObject>>(schema: yup.ObjectSchema<T>, value: T) => {
        let valid = true;
        try {
            schema.validateSync(value);
        } catch {
            valid = false;
        }
        return valid;
    };

    return {
        formSchema,
        consentSchema: consentCheckbox,
        creditCardSchema: yup.object({ ...creditCardSchema }),
        userDataSchema: yup.object({ ...userDataSchema }),
        isValidSchema: isValidFn,
    };
};

export interface CheckoutFormData {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    settlement: string | undefined;
    postalCode: string | undefined;
    address: string | undefined;
    consentCheckbox: ConsentType;
    creditCardFirstName: string | undefined;
    creditCardLastName: string | undefined;
    creditCardNumber: string | undefined;
    creditCardExpirationDate: string | undefined;
}
